import React, { useRef, useState, useMemo, useCallback, useEffect } from 'react'
import classnames from 'classnames'
import { createPortal } from 'react-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import Datepicker from 'react-tailwindcss-datepicker'
import { ArrowLeftIcon, ExclamationCircleIcon, GiftIcon } from '@heroicons/react/24/outline'
import Button from '../../../../../components/Form/Button'
import Input from '../../../../../components/Form/Input'
import Panel from '../../../../../components/Panel'
import { getInputStyles } from '../../../../../components/Form/styles'
import { InstallmentConfigurator } from './InstallmentConfigurator'
import { createKingIncentivePlanApi } from '../../../../../apis/apis'
import { pushToast } from '../../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../../shared/redux/hooks'
import { formatDateForApi, getApiErrorMessage } from '../../../../../util/string'
import { equalSplit } from './installmentMath'
import {
    computeSuggestedFirstDueDate,
    parseIsoDateLocal,
} from './installmentDates'
import { useTodayMidnight } from './useTodayMidnight'

const DESCRIPTION_SUGGESTIONS = [
    'AutoPay Incentive',
    'Courtesy Credit – Billing Error',
    'Premature Billing Credit',
    'Balance Adjustment',
    'Payment Plan Credit',
    'Deposit Refund',
]

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)

/** Safely format any ISO date or datetime string to "Mon D, YYYY". */
const formatShortDate = (iso: string) => {
    const datePart = iso.slice(0, 10)
    return new Date(datePart + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

type AmountMode = 'total' | 'per-installment' | 'freeform'
const COMMON_CADENCES = [1, 3, 6, 12]

interface LastKingInvoice {
    billingStartDate?: string
    billingEndDate?: string
    dueDate?: string
}

interface CreateCreditFormProps {
    tenantAccountId: string
    estimatedUtilityBalance?: number
    kingOnlyBalance?: number
    hasCreateKingCreditPermission: boolean
    hasCreateUtilityCreditPermission: boolean
    lastKingInvoice?: LastKingInvoice
    onSuccess: () => void
    onCancel: () => void
}

const CreateCreditForm: React.FC<CreateCreditFormProps> = ({
    tenantAccountId,
    estimatedUtilityBalance: _estimatedUtilityBalance,
    kingOnlyBalance,
    hasCreateKingCreditPermission,
    hasCreateUtilityCreditPermission,
    lastKingInvoice,
    onSuccess,
    onCancel,
}) => {
    const dispatch = useAppDispatch()
    const suggestedFirstCreditDate = useMemo(
        () => computeSuggestedFirstDueDate(lastKingInvoice?.dueDate),
        [lastKingInvoice?.dueDate]
    )
    const todayMidnight = useTodayMidnight()
    const [amountMode, setAmountMode] = useState<AmountMode>('total')
    const [perInstallmentStr, setPerInstallmentStr] = useState('')
    const [installments, setInstallments] = useState<number[]>([])
    const [editedIndices, setEditedIndices] = useState<Set<number>>(new Set())
    const [showConfirm, setShowConfirm] = useState(false)
    const [showCustomCadence, setShowCustomCadence] = useState(false)
    const confirmLockedRef = useRef(false)

    const canCreate = hasCreateKingCreditPermission || hasCreateUtilityCreditPermission

    const validationSchema = useMemo(
        () =>
            Yup.object({
                description: Yup.string().trim().required('Description is required').min(1, 'Description is required'),
                cadence: Yup.number()
                    .typeError('Cadence must be a number')
                    .required('Cadence is required')
                    .min(1, 'Minimum cadence is 1 month')
                    .max(12, 'Maximum cadence is 12 months')
                    .integer('Cadence must be a whole number'),
                totalAmount: Yup.number()
                    .typeError('Amount must be a number')
                    .required('Amount is required')
                    .moreThan(0, 'Amount must be greater than $0'),
                firstCreditDate: Yup.string().required('First credit date is required'),
            }),
        []
    )

    const formik = useFormik({
        initialValues: {
            description: '',
            cadence: '1',
            totalAmount: '',
            firstCreditDate: suggestedFirstCreditDate,
        },
        validationSchema,
        validateOnChange: true,
        validateOnBlur: true,
        onSubmit: async (values) => {
            const cadenceNum = parseInt(values.cadence) || 1
            const effectiveTotal =
                amountMode === 'freeform'
                    ? Math.round(installments.reduce((s, a) => s + a, 0) * 100) / 100
                    : amountMode === 'per-installment'
                        ? Math.round((parseFloat(perInstallmentStr) || 0) * cadenceNum * 100) / 100
                        : parseFloat(values.totalAmount) || 0

            const finalInstallments = cadenceNum === 1 ? [effectiveTotal] : installments

            if (finalInstallments.some((v) => v <= 0)) {
                dispatch(pushToast({ message: 'All installments must be greater than $0', type: 'error' }))
                return
            }

            if (amountMode === 'total' && cadenceNum > 1) {
                const installmentsTotal = installments.reduce((s, a) => s + a, 0)
                if (effectiveTotal > 0 && Math.abs(installmentsTotal - effectiveTotal) > 0.005) {
                    dispatch(pushToast({ message: 'Installment total must equal credit amount', type: 'error' }))
                    return
                }
            }

            try {
                const cadenceEnum = cadenceNum === 1 ? 'ONE_TIME' : 'MONTHLY'
                await createKingIncentivePlanApi(
                    {
                        startingBalance: effectiveTotal.toFixed(2),
                        cadence: cadenceEnum,
                        installmentCount: cadenceNum,
                        planType: 'ACCOMMODATION_CREDIT',
                        memo: values.description.trim(),
                        ...(values.firstCreditDate ? { startDate: values.firstCreditDate } : {}),
                        ...(cadenceNum > 1 ? { installmentAmounts: finalInstallments.map(a => a.toFixed(2)) } : {}),
                    },
                    tenantAccountId
                )
                await onSuccess()
                dispatch(pushToast({ message: 'Accommodation credit added successfully', type: 'success' }))
            } catch (e: any) {
                setShowConfirm(false)
                confirmLockedRef.current = false
                dispatch(
                    pushToast({
                        message: 'Failed to add accommodation credit',
                        type: 'error',
                        description: getApiErrorMessage(e),
                    })
                )
            }
        },
    })

    const cadence = parseInt(formik.values.cadence) || 1

    const firstCreditDateObj = useMemo(
        () => parseIsoDateLocal(formik.values.firstCreditDate),
        [formik.values.firstCreditDate]
    )

    const isFirstCreditInPast = useMemo(() => {
        if (firstCreditDateObj == null) return false
        return firstCreditDateObj < todayMidnight
    }, [firstCreditDateObj, todayMidnight])

    const totalAmount = useMemo(() => {
        if (amountMode === 'freeform') return Math.round(installments.reduce((s, a) => s + a, 0) * 100) / 100
        if (amountMode === 'total') return parseFloat(formik.values.totalAmount) || 0
        return Math.round((parseFloat(perInstallmentStr) || 0) * cadence * 100) / 100
    }, [amountMode, formik.values.totalAmount, perInstallmentStr, cadence, installments])

    const perInstallmentTotal = useMemo(
        () => Math.round((parseFloat(perInstallmentStr) || 0) * cadence * 100) / 100,
        [perInstallmentStr, cadence]
    )

    const freeformTotal = useMemo(
        () => Math.round(installments.reduce((s, a) => s + a, 0) * 100) / 100,
        [installments]
    )

    const effectiveTotalForConfigurator = useMemo(() => {
        if (amountMode === 'per-installment') return perInstallmentTotal
        if (amountMode === 'freeform') return freeformTotal
        return totalAmount
    }, [amountMode, perInstallmentTotal, freeformTotal, totalAmount])

    useEffect(() => {
        if (amountMode === 'per-installment' && perInstallmentTotal > 0) {
            formik.setFieldValue('totalAmount', perInstallmentTotal.toString(), true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perInstallmentTotal, amountMode])

    useEffect(() => {
        if (amountMode === 'freeform' && freeformTotal > 0) {
            formik.setFieldValue('totalAmount', freeformTotal.toString(), true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeformTotal, amountMode])

    useEffect(() => {
        if (amountMode === 'freeform') return
        if (cadence <= 0) return
        if (cadence === 1) {
            setInstallments([totalAmount])
            setEditedIndices(new Set())
            return
        }
        if (editedIndices.size === 0) {
            setInstallments(equalSplit(totalAmount, cadence))
        }
    }, [totalAmount, cadence, amountMode, editedIndices.size])

    const handleCadenceChange = useCallback(
        (newCadence: number) => {
            const clamped = Math.max(1, Math.min(12, newCadence))
            formik.setFieldValue('cadence', clamped.toString())
            setEditedIndices(new Set())
            if (amountMode === 'freeform') {
                setInstallments(Array(clamped).fill(0))
            } else if (clamped === 1) {
                setInstallments([totalAmount])
            } else {
                setInstallments(equalSplit(totalAmount, clamped))
            }
        },
        [totalAmount, amountMode, formik]
    )

    const exceedsKingBalance =
        kingOnlyBalance !== undefined &&
        effectiveTotalForConfigurator > 0 &&
        effectiveTotalForConfigurator > kingOnlyBalance

    const hasInstallmentIssue = useMemo(() => {
        if (cadence === 1) return false
        if (installments.length === 0) return false
        const hasZero = installments.some((v) => v <= 0)
        if (amountMode === 'freeform') return hasZero || freeformTotal <= 0
        const refTotal = amountMode === 'per-installment' ? perInstallmentTotal : totalAmount
        const sum = installments.reduce((s, a) => s + a, 0)
        const mismatch = amountMode === 'total' && refTotal > 0 && Math.abs(sum - refTotal) > 0.005
        return hasZero || mismatch || refTotal <= 0
    }, [cadence, installments, amountMode, totalAmount, perInstallmentTotal, freeformTotal])

    const handleSubmitClick = async () => {
        const errors = await formik.validateForm()
        await formik.setTouched(Object.fromEntries(Object.keys(formik.values).map((k) => [k, true])))
        if (
            Object.keys(errors).length === 0 &&
            !hasInstallmentIssue &&
            !exceedsKingBalance &&
            !isFirstCreditInPast
        ) {
            setShowConfirm(true)
        }
    }

    const handleConfirmSubmit = async () => {
        if (confirmLockedRef.current || formik.isSubmitting) return
        confirmLockedRef.current = true
        await formik.submitForm()
    }

    if (!canCreate) {
        return (
            <div className="space-y-4">
                <button
                    type="button"
                    onClick={onCancel}
                    className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Back to Incentives
                </button>
                <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <ExclamationCircleIcon className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">
                        You do not have permission to add accommodation credits.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {/* Confirmation modal */}
            {showConfirm && createPortal(
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-md w-full p-6 space-y-5">
                        <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 shrink-0">
                                <GiftIcon className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add Accommodation Credit?</h3>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Please confirm the details below before submitting.</p>
                            </div>
                        </div>
                        <div className="space-y-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-200 dark:divide-slate-700">
                            <div className="flex justify-between px-4 py-2">
                                <span className="text-gray-500 dark:text-gray-400">Amount</span>
                                <span className="font-medium dark:text-gray-200 text-emerald-600 dark:text-emerald-400">
                                    {formatCurrency(totalAmount)}
                                </span>
                            </div>
                            <div className="flex justify-between px-4 py-2">
                                <span className="text-gray-500 dark:text-gray-400">Cadence</span>
                                <span className="font-medium dark:text-gray-200">
                                    {cadence === 1 ? 'One-time' : `${cadence} monthly installments`}
                                </span>
                            </div>
                            {firstCreditDateObj && (
                                <div className="flex justify-between px-4 py-2">
                                    <span className="text-gray-500 dark:text-gray-400">First Credit Date</span>
                                    <span className="font-medium dark:text-gray-200">
                                        {firstCreditDateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between items-start px-4 py-2">
                                <span className="text-gray-500 dark:text-gray-400 shrink-0 mr-4">Description</span>
                                <span className="font-medium dark:text-gray-200 text-right">{formik.values.description}</span>
                            </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button color="slate" onClick={() => setShowConfirm(false)} disabled={formik.isSubmitting}>
                                Go back
                            </Button>
                            <Button
                                color="emerald"
                                isSaving={formik.isSubmitting}
                                disabled={formik.isSubmitting}
                                onClick={handleConfirmSubmit}
                            >
                                {formik.isSubmitting ? 'Adding credit...' : 'Confirm & add credit'}
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <button
                type="button"
                onClick={onCancel}
                className="flex items-center gap-1 rounded-md border border-indigo-600 dark:border-indigo-400 px-3 py-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
            >
                <ArrowLeftIcon className="w-4 h-4" />
                Back to Incentives
            </button>

            <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-8 h-8 rounded-md bg-emerald-100 dark:bg-emerald-900/40 shrink-0">
                    <GiftIcon className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Accommodation Credit</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                        Issue a credit for service interruptions or other accommodations
                    </p>
                </div>
            </div>

            <form onSubmit={formik.handleSubmit} className="space-y-6">
                <Panel header="Credit Configuration">
                    <div className="space-y-5 pt-2">

                        {/* Billing context banner */}
                        {kingOnlyBalance === undefined ? (
                            <div className="animate-pulse flex items-center justify-between p-3 bg-slate-100 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-700 rounded-lg">
                                <div className="space-y-1.5">
                                    <div className="h-3 w-36 bg-slate-300 dark:bg-slate-600 rounded" />
                                    <div className="h-5 w-24 bg-slate-300 dark:bg-slate-600 rounded" />
                                </div>
                            </div>
                        ) : (
                            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg space-y-2">
                                <div>
                                    <p className="text-sm text-blue-800 dark:text-blue-300">Tenant King Energy balance</p>
                                    <p className="text-lg font-semibold text-blue-900 dark:text-blue-200">
                                        {formatCurrency(kingOnlyBalance)}
                                    </p>
                                </div>
                                {lastKingInvoice?.dueDate && (
                                    <p className="text-xs text-blue-700 dark:text-blue-400">
                                        Last billing period:
                                        {lastKingInvoice.billingStartDate && lastKingInvoice.billingEndDate
                                            ? <> {formatShortDate(lastKingInvoice.billingStartDate)} – {formatShortDate(lastKingInvoice.billingEndDate)}</>
                                            : null
                                        }
                                        {' · '}Statement: {formatShortDate(
                                            (() => {
                                                const d = new Date(lastKingInvoice.dueDate.slice(0, 10) + 'T00:00:00')
                                                d.setDate(d.getDate() - 14)
                                                return d.toISOString().split('T')[0]
                                            })()
                                        )}
                                        {' · '}Due: {formatShortDate(lastKingInvoice.dueDate)}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Description */}
                        <div>
                            <Input
                                label="Description"
                                name="description"
                                value={formik.values.description}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                placeholder="e.g., AutoPay Incentive"
                                required
                                error={formik.touched.description && formik.errors.description ? formik.errors.description : undefined}
                            />
                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                <span className="text-xs text-slate-500 dark:text-slate-400 shrink-0">Suggestions:</span>
                                {DESCRIPTION_SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => formik.setFieldValue('description', s)}
                                        className="px-2 py-0.5 text-xs rounded-full border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer"
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Cadence */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                Credit Cadence <span className="text-red-500">*</span>
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                {COMMON_CADENCES.map((c) => (
                                    <button
                                        key={c}
                                        type="button"
                                        onClick={() => {
                                            setShowCustomCadence(false)
                                            handleCadenceChange(c)
                                        }}
                                        className={`px-3 py-1 rounded-full text-sm border cursor-pointer transition-colors ${!showCustomCadence && cadence === c
                                            ? 'bg-indigo-600 text-white border-indigo-600'
                                            : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                            }`}
                                    >
                                        {c === 1 ? 'One-time' : `${c} mo`}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowCustomCadence(true)
                                        formik.setFieldValue('cadence', '')
                                    }}
                                    className={`px-3 py-1 rounded-full text-sm border cursor-pointer transition-colors ${showCustomCadence
                                        ? 'bg-indigo-600 text-white border-indigo-600'
                                        : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }`}
                                >
                                    Other
                                </button>
                                {showCustomCadence && (
                                    <Input
                                        label={undefined}
                                        type="number"
                                        value={formik.values.cadence}
                                        onChange={(e) => handleCadenceChange(parseInt(e.target.value) || 1)}
                                        onBlur={formik.handleBlur}
                                        name="cadence"
                                        placeholder="months"
                                        className="w-24"
                                        error={formik.touched.cadence && formik.errors.cadence ? formik.errors.cadence : undefined}
                                    />
                                )}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {cadence === 1
                                    ? 'Credit applied as a single amount.'
                                    : `Credit distributed over ${cadence} monthly installments.`}
                            </p>
                        </div>

                        {/* First Credit Date */}
                        <div className="space-y-1 max-w-xs">
                            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                First Credit Date <span className="text-red-500">*</span>
                            </label>
                            <Datepicker
                                useRange={false}
                                asSingle={true}
                                value={{
                                    startDate: firstCreditDateObj,
                                    endDate: firstCreditDateObj,
                                }}
                                onChange={(value: any) => {
                                    const picked: Date | string | null = value?.startDate ?? null
                                    if (picked == null) {
                                        formik.setFieldValue('firstCreditDate', '')
                                        return
                                    }
                                    const asDate = picked instanceof Date ? picked : new Date(picked)
                                    if (!isNaN(asDate.getTime())) {
                                        formik.setFieldValue('firstCreditDate', formatDateForApi(asDate))
                                    }
                                }}
                                primaryColor={'indigo'}
                                inputClassName={classnames(getInputStyles(), 'inline-block w-full')}
                                popoverDirection="down"
                                displayFormat={'MMM D, YYYY'}
                                minDate={todayMidnight}
                            />
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                {cadence === 1
                                    ? 'Date the credit will be applied.'
                                    : 'Subsequent credits are applied monthly from this date.'}
                            </p>
                        </div>

                        {isFirstCreditInPast && (
                            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                                <ExclamationCircleIcon className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                                <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                    First credit date is in the past. Please choose a date on or after today.
                                </p>
                            </div>
                        )}

                        {/* Amount entry */}
                        {cadence === 1 ? (
                            <Input
                                label="Amount ($)"
                                type="number"
                                value={formik.values.totalAmount}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                name="totalAmount"
                                placeholder="0.00"
                                required
                                error={
                                    exceedsKingBalance
                                        ? `You may not credit more than what's owed to King Energy (${formatCurrency(kingOnlyBalance!)})`
                                        : formik.touched.totalAmount && formik.errors.totalAmount
                                            ? formik.errors.totalAmount as string
                                            : undefined
                                }
                                className="max-w-xs"
                            />
                        ) : (
                            <>
                                {/* Amount mode toggle */}
                                <div className="space-y-2">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                        Amount Entry Mode
                                    </label>
                                    <div className="flex gap-2 flex-wrap">
                                        {(['total', 'per-installment', 'freeform'] as AmountMode[]).map((mode) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                onClick={() => {
                                                    setAmountMode(mode)
                                                    if (mode !== 'per-installment') setPerInstallmentStr('')
                                                    if (mode !== 'total') formik.setFieldValue('totalAmount', '')
                                                    setEditedIndices(new Set())
                                                }}
                                                className={`px-3 py-1.5 rounded text-sm border cursor-pointer transition-colors ${amountMode === mode
                                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                                    : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-slate-700'
                                                    }`}
                                            >
                                                {mode === 'total' ? 'Total amount' : mode === 'per-installment' ? 'Per installment' : 'Freeform'}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {amountMode === 'total' && (
                                    <div>
                                        <Input
                                            label="Total Credit Amount ($)"
                                            type="number"
                                            value={formik.values.totalAmount}
                                            onChange={(e) => {
                                                formik.handleChange(e)
                                                setEditedIndices(new Set())
                                            }}
                                            onBlur={formik.handleBlur}
                                            name="totalAmount"
                                            placeholder="0.00"
                                            required
                                            className="max-w-xs"
                                            error={
                                                exceedsKingBalance
                                                    ? `You may not credit more than what's owed to King Energy (${formatCurrency(kingOnlyBalance!)})`
                                                    : formik.touched.totalAmount && formik.errors.totalAmount
                                                        ? formik.errors.totalAmount as string
                                                        : undefined
                                            }
                                        />
                                        {totalAmount > 0 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                ≈ {formatCurrency(totalAmount / cadence)} per installment
                                            </p>
                                        )}
                                    </div>
                                )}

                                {amountMode === 'per-installment' && (
                                    <div>
                                        <Input
                                            label="Amount Per Installment ($)"
                                            type="number"
                                            value={perInstallmentStr}
                                            onChange={(e) => {
                                                setPerInstallmentStr(e.target.value)
                                                setEditedIndices(new Set())
                                            }}
                                            placeholder="0.00"
                                            className="max-w-xs"
                                        />
                                        {perInstallmentTotal > 0 && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                Total: {formatCurrency(perInstallmentTotal)} ({cadence} installments × {formatCurrency(parseFloat(perInstallmentStr) || 0)})
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* Installment configurator */}
                                <InstallmentConfigurator
                                    totalAmount={effectiveTotalForConfigurator}
                                    count={cadence}
                                    firstDueDate={formik.values.firstCreditDate || undefined}
                                    installments={installments}
                                    onInstallmentsChange={setInstallments}
                                    editedIndices={editedIndices}
                                    onEditedIndicesChange={setEditedIndices}
                                    freeform={amountMode === 'freeform'}
                                    totalLabel="Target total"
                                />

                                {exceedsKingBalance && amountMode !== 'total' && (
                                    <p className="text-sm text-red-600 dark:text-red-400">
                                        You may not credit more than what's owed to King Energy ({formatCurrency(kingOnlyBalance!)})
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </Panel>

                <div className="flex justify-end gap-3">
                    <Button color="slate" onClick={onCancel} disabled={formik.isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        color="emerald"
                        disabled={formik.isSubmitting || hasInstallmentIssue || exceedsKingBalance || isFirstCreditInPast || !formik.values.firstCreditDate}
                        onClick={handleSubmitClick}
                    >
                        Add credit
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default CreateCreditForm
